import { useMemo, type ReactNode } from 'react';
import { SEO } from '@/components/SEO';
import { hqFromSatelliteUrl } from '@/hq/urls';
import {
  harborHomeUrl,
  harborSatelliteUrl,
  satelliteCanonicalBase,
  type SatelliteConfig,
  type SatelliteFaq,
} from './config';
import { trackSatelliteAction, useSatelliteScreenEvent } from './analytics';

function faqJsonLd(faqs: SatelliteFaq[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };
}

function webAppJsonLd(config: SatelliteConfig, canonical: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: config.name,
    url: canonical,
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'Web',
    description: config.description,
    publisher: {
      '@type': 'Organization',
      name: 'Harbor',
      url: 'https://harborcoparent.com',
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
  };
}

export function SatelliteLayout({
  config,
  children,
}: {
  config: SatelliteConfig;
  children: ReactNode;
}) {
  useSatelliteScreenEvent(config);
  const baseUrl = satelliteCanonicalBase(config.apex);
  const canonical = `${baseUrl}/`;
  const harborTool = harborSatelliteUrl(config);
  const harborHome = harborHomeUrl(config.apex);
  const jsonLd = useMemo(
    () => [webAppJsonLd(config, canonical), faqJsonLd(config.faqs)],
    [config, canonical],
  );

  return (
    <div className='min-h-screen bg-white text-gray-900'>
      <SEO
        title={config.title}
        description={config.description}
        path='/'
        baseUrl={baseUrl}
        siteName={config.name}
        jsonLd={jsonLd}
      />
      <header className='border-b border-gray-100 print:hidden'>
        <div className='mx-auto flex max-w-3xl items-center justify-between px-4 py-4'>
          <p className='text-lg font-semibold tracking-tight'>{config.name}</p>
          <a
            href={harborHome}
            className='text-sm text-primary hover:underline'
            onClick={() => trackSatelliteAction('Satellite Harbor CTA Pressed', config, { location: 'header' })}
          >
            Built by Harbor
          </a>
        </div>
      </header>

      <main className='mx-auto max-w-3xl px-4 py-10'>
        <p className='text-sm font-medium uppercase tracking-wide text-primary print:hidden'>
          Free tool
        </p>
        <h1 className='mt-2 text-3xl font-semibold tracking-tight sm:text-4xl'>{config.name}</h1>
        <p className='mt-3 text-lg text-gray-600 print:hidden'>{config.tagline}</p>

        <div className='mt-8'>{children}</div>

        <aside className='mt-10 rounded-2xl bg-sky-50 px-5 py-4 print:hidden'>
          <p className='text-sm text-gray-700'>
            Want this to stay in sync for both parents?{' '}
            <a
              href={harborTool}
              className='font-semibold text-primary hover:underline'
              onClick={() =>
                trackSatelliteAction('Satellite Harbor CTA Pressed', config, { location: 'tool-cta' })
              }
            >
              {config.harborCta}
            </a>
            .
          </p>
        </aside>

        <section className='mt-12 print:hidden'>
          <h2 className='text-xl font-semibold'>Questions</h2>
          <dl className='mt-4 space-y-5'>
            {config.faqs.map((faq) => (
              <div key={faq.question}>
                <dt className='font-medium'>{faq.question}</dt>
                <dd className='mt-1 text-gray-600'>{faq.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      </main>

      <footer className='border-t border-gray-100 print:hidden'>
        <div className='mx-auto max-w-3xl space-y-2 px-4 py-8 text-sm text-gray-500'>
          <p>
            Built by the team behind{' '}
            <a
              href={harborHome}
              className='text-primary hover:underline'
              onClick={() =>
                trackSatelliteAction('Satellite Harbor CTA Pressed', config, { location: 'footer' })
              }
            >
              Harbor
            </a>
            . {config.name} is a free standalone tool on {config.apex}.
          </p>
          <p>
            More coparent tools at{' '}
            <a
              href={hqFromSatelliteUrl(config.apex)}
              className='text-primary hover:underline'
              onClick={() =>
                trackSatelliteAction('Satellite HQ CTA Pressed', config, { location: 'footer' })
              }
            >
              Co-Parent HQ
            </a>
            .
          </p>
        </div>
      </footer>
    </div>
  );
}
